import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

// ── Bare student (existing, used elsewhere) ──────────────────────────────────
export interface Student {
  id: number;
  user_id: number;
  school_id: number;
  counselor_id: number;
  grade_level: number;
  School?: { id: number; name: string };
  counselor?: { id: number; first_name: string; last_name: string; preferredName: string | null };
}

// ── Enriched student returned by GET /api/counselors/me/students ─────────────
export interface CounselorStudent {
  id: number;
  user_id: number;
  school_id: number;
  counselor_id: number;
  grade_level: number;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    preferred_name: string | null;
    email: string;
    role: string;
    is_active: boolean;
  };
  Profile: {
    id: number;
    bio: string | null;
    profile_picture_path: string | null;
    profile_complete_percent: number;
    hobbies_and_interests: string | null;
    avatar_id: number | null;
    Career: {
      id: number;
      name: string;
      icon: string | null;
    } | null;
  } | null;
  School: {
    id: number;
    name: string;
    district: string;
  } | null;
}

// ── Grade helpers ─────────────────────────────────────────────────────────────
export const GRADE_LABELS: Record<number, string> = {
  9: 'Freshman',
  10: 'Sophomore',
  11: 'Junior',
  12: 'Senior',
};

// Derive a display status from profile completion — no status field in DB
export function deriveStatus(pct: number): 'on-track' | 'needs-attention' | 'new' {
  if (pct === 0) return 'new';
  if (pct < 50) return 'needs-attention';
  return 'on-track';
}

@Injectable({ providedIn: 'root' })
export class StudentService {
  /** Base API path for all student endpoints. */
  private base = '/api/students';
  private counselorBase = '/api/counselors';

  constructor(private http: HttpClient) {}

  /**
   * Fetches a single student record by its ID.
   * @param {number} id - The ID of the student to fetch.
   * @returns {Observable<Student>}
   */
  getStudent(id: number): Observable<Student> {
    return this.http.get<Student>(`${this.base}/${id}`);
  }

  /**
   * Fetches a student record by the associated user ID.
   * @param {number} userId - The user ID to look up.
   * @returns {Observable<Student>}
   */
  getStudentByUserId(userId: number): Observable<Student> {
    return this.http.get<Student>(`${this.base}/by-user/${userId}`);
  }

  /**
   * Fetches all student records.
   * @returns {Observable<Student[]>}
   */
  getAllStudents(): Observable<Student[]> {
    return this.http.get<Student[]>(this.base);
  }

  /**
   * Updates a student record by its ID.
   * @param {number} id - The ID of the student to update.
   * @param {Partial<Student>} data - The fields to update.
   * @returns {Observable<Student>}
   */
  updateStudent(id: number, data: Partial<Student>): Observable<Student> {
    return this.http.put<Student>(`${this.base}/${id}`, data);
  }

  /**
   * Creates a new student record associated with a user.
   * @param {number} userId - The user ID to associate the student record with.
   * @returns {Observable<Student>}
   */
  createStudent(userId: number): Observable<Student> {
    return this.http.post<Student>(this.base, { user_id: userId });
  }

  /**
   * Deletes a student record by its ID.
   * @param {number} id - The ID of the student to delete.
   * @returns {Observable<{ message: string }>}
   */
  deleteStudent(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.base}/${id}`);
  }

  getMyStudents(): Observable<CounselorStudent[]> {
    return this.http.get<CounselorStudent[]>(`${this.counselorBase}/me/students`);
  }
}
